import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { db } from '../firebaseConfig';
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { storage } from '../firebaseConfig'; // Import storage
import { ref, uploadBytesResumable, getDownloadURL, listAll, deleteObject } from 'firebase/storage'; // Import storage functions
import './AdminStyles.css'; // Import admin specific styles
import AdminTestimonials from './AdminTestimonials'; // Import the new testimonials admin page

function AdminDashboard() {
    const [activeTab, setActiveTab] = useState('portfolio'); // 'portfolio', 'products', 'testimonials', or 'clients-logos'
    const [activeType, setActiveType] = useState('mobile'); // 'mobile' or 'websites'

    const [loading, setLoading] = useState(true);
    const [successMessage, setSuccessMessage] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);

    const [projectsList, setProjectsList] = useState([]); // State to hold the list of projects
    const [listLoading, setListLoading] = useState(false); // Loading state for the list

    // Client logos state
    const [clientLogos, setClientLogos] = useState([]);
    const [logosLoading, setLogosLoading] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [logoUploadProgress, setLogoUploadProgress] = useState(0);

    const [isAddingProject, setIsAddingProject] = useState(false); // State for modal visibility
    const [editingProject, setEditingProject] = useState(null); // State to hold project being edited

    const [formData, setFormData] = useState({
        name: '',
        type: 'mobile',
        collection: 'portfolio',
        category: '',
        description: '',
        banner: '',
        image: '',
        intro: '',
        features: [], // Make features optional by starting with empty array
        platforms: [''],
        primaryColor: '',
        secondaryColor: '',
        accentColor: '',
        prototype: '',
        productImages: [''],
        align: 'left',
    });
    const [previewMode, setPreviewMode] = useState(false);
    const [previewCollection, setPreviewCollection] = useState('portfolio');
    const [validationErrors, setValidationErrors] = useState({});

    const [uploadingBanner, setUploadingBanner] = useState(false);
    const [bannerUploadProgress, setBannerUploadProgress] = useState(0);
    const [uploadingProductImages, setUploadingProductImages] = useState([]); // Array of booleans
    const [productImageUploadProgress, setProductImageUploadProgress] = useState([]); // Array of numbers

    const [uploadingProjectImage, setUploadingProjectImage] = useState(false);
    const [projectImageUploadProgress, setProjectImageUploadProgress] = useState(0);

    const defaultPhases = useMemo(() => ({
        mobile: [
            'Concept & Planning - Defining the app\'s purpose, features, and target audience. Gathering requirements and sketching out initial ideas.',
            'UI/UX Design - Designing an intuitive and visually appealing interface that ensures a seamless user experience.',
            'Development & Testing - Writing the code, integrating features, and conducting thorough testing to ensure functionality, security, and reliability.',
            'Deployment & Publishing - Preparing the application for release on iOS and Android platforms. Setting up backend services and security measures.'
        ],
        website: [
            'Discovery & Planning - Understanding client needs, defining goals, and creating a project roadmap.',
            'Design & Wireframing - Creating visual designs and interactive prototypes for client approval.',
            'Development - Building the website with responsive design and required functionality.',
            'Testing & Launch - Quality assurance testing and deploying the website to production.'
        ]
    }), []);

    // Update phases when type changes
    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            phases: defaultPhases[activeType]
        }));
    }, [activeType, defaultPhases]);

    // Fetch client logos from Firebase Storage
    const fetchClientLogos = useCallback(async () => {
        setLogosLoading(true);
        try {
            const clientsRef = ref(storage, 'clients');
            const result = await listAll(clientsRef);
            
            // Get download URLs for all images
            const logoPromises = result.items.map(async (item) => {
                const url = await getDownloadURL(item);
                return {
                    name: item.name,
                    url,
                    path: item.fullPath,
                    visible: true // Default to visible
                };
            });
            
            const logos = await Promise.all(logoPromises);
            
            // Fetch visibility states from Firestore
            try {
                const visibilityRef = collection(db, 'clientLogosVisibility');
                const visibilitySnapshot = await getDocs(visibilityRef);
                const visibilityData = {};
                
                visibilitySnapshot.forEach((doc) => {
                    visibilityData[doc.id] = doc.data().visible;
                });
                
                // Update logos with their visibility states
                const logosWithVisibility = logos.map(logo => ({
                    ...logo,
                    visible: visibilityData[logo.name] !== undefined ? visibilityData[logo.name] : true
                }));
                
                setClientLogos(logosWithVisibility);
            } catch (error) {
                console.error('Error fetching visibility states:', error);
                setClientLogos(logos); // Use default visibility if Firestore fails
            }
        } catch (error) {
            console.error('Error fetching client logos:', error);
            setErrorMessage('Failed to fetch client logos');
        } finally {
            setLogosLoading(false);
        }
    }, []);

    // Fetch client logos when tab is active
    useEffect(() => {
        if (activeTab === 'clients-logos') {
            fetchClientLogos();
        }
    }, [activeTab, fetchClientLogos]);

    const fetchProjects = useCallback(async () => {
        if (activeTab !== 'portfolio' && activeTab !== 'products') return;
        setListLoading(true);
        try {
            const projectsRef = collection(db, 'projects');
            const snapshot = await getDocs(projectsRef);
            const projectsData = {
                portfolio: { mobile: [], websites: [] },
                products: { mobile: [], websites: [] }
            };

            snapshot.forEach((doc) => {
                const project = { id: doc.id, ...doc.data() };
                const collection = project.collection || 'portfolio';
                const type = project.type || 'mobile';
                projectsData[collection][type === 'mobile' ? 'mobile' : 'websites'].push(project);
            });

            setProjectsList(projectsData[activeTab][activeType]);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching projects:', error);
            setErrorMessage('Failed to fetch projects');
            setLoading(false);
        } finally {
            setListLoading(false);
        }
    }, [activeTab, activeType]);

    useEffect(() => {
        if (activeTab === 'portfolio' || activeTab === 'products') {
            fetchProjects();
        }
    }, [fetchProjects, activeTab]);

    const validateForm = () => {
        const errors = {};

        if (!formData.name.trim()) errors.name = 'Project name is required';
        if (!formData.category.trim()) errors.category = 'Project category is required';
        if (!formData.description.trim()) errors.description = 'Project description is required';

        // Banner image validation
        if (!formData.banner || !formData.banner.url) errors.banner = 'Banner image is required';
        // Project image validation
        if (!formData.image || !formData.image.url) errors.image = 'Project image is required';

        if (!formData.intro.trim()) errors.intro = 'Project introduction is required';

        // Features are now optional
        if (formData.features.length > 0) {
            formData.features.forEach((feature, index) => {
                if (!feature.title.trim()) {
                    errors[`featureTitle${index}`] = 'Feature title is required if description is provided';
                }
                if (!feature.description.trim()) {
                    errors[`featureDesc${index}`] = 'Feature description is required if title is provided';
                }
            });
        }

        // Validate platforms
        if (!formData.platforms.length || !formData.platforms[0].trim()) {
            errors.platforms = 'At least one platform is required';
        }

        // Validate colors - only primary and secondary are required
        if (!formData.primaryColor.trim()) errors.primaryColor = 'Primary color is required';
        if (!formData.secondaryColor.trim()) errors.secondaryColor = 'Secondary color is required';
        // Accent color is optional

        // Validate prototype
        if (!formData.prototype.trim()) errors.prototype = 'Prototype iframe code is required';

        // Validate product images (at least one with a url)
        if (!formData.productImages.length || !formData.productImages[0].url) {
            errors.productImages = 'At least one product image is required';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleEdit = (project) => {
        setEditingProject(project);
        setFormData({
            name: project.name,
            type: project.type,
            banner: project.banner && project.banner.url ? project.banner : (typeof project.banner === 'string' && project.banner ? { url: project.banner, name: '' } : null),
            image: project.image && project.image.url ? project.image : (typeof project.image === 'string' && project.image ? { url: project.image, name: '' } : null),
            intro: project.intro,
            features: project.features,
            platforms: project.platforms,
            primaryColor: project.primaryColor,
            secondaryColor: project.secondaryColor,
            accentColor: project.accentColor,
            prototype: project.prototype,
            productImages: Array.isArray(project.productImages) ? project.productImages.map(img => img && img.url ? img : (typeof img === 'string' && img ? { url: img, name: '' } : {})) : [{}],
            align: project.align,
            collection: project.collection || 'portfolio',
            category: project.category || '',
            description: project.description || '',
        });
        setIsAddingProject(true);
    };

    const handleToggleVisibility = async (projectId, currentVisibility) => {
        try {
            const projectRef = doc(db, 'projects', projectId);
            await updateDoc(projectRef, { visible: !currentVisibility, status: !currentVisibility ? 'published' : 'draft' });
            setSuccessMessage('Project visibility updated successfully!');
            await fetchProjects();
        } catch (error) {
            console.error('Error toggling visibility:', error);
            setErrorMessage('Failed to update project visibility');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Prevent submission if any upload is in progress
        if (anyUploading) {
            return false;
        }

        if (!validateForm()) {
            return false;
        }

        try {
            const projectData = {
                ...formData,
                updatedAt: serverTimestamp(),
            };

            if (editingProject) {
                // Remove createdAt if present, don't overwrite original creation time
                delete projectData.createdAt;
                const projectRef = doc(db, 'projects', editingProject.id);
                await updateDoc(projectRef, projectData);
                setSuccessMessage('Project updated successfully!');
            } else {
                projectData.createdAt = serverTimestamp();
                await addDoc(collection(db, 'projects'), projectData);
                setSuccessMessage('Project added successfully!');
            }

            // Reset form
            setFormData({
                name: '',
                type: 'mobile',
                collection: 'portfolio',
                category: '',
                description: '',
                banner: '',
                image: '',
                intro: '',
                features: [],
                platforms: [''],
                primaryColor: '',
                secondaryColor: '',
                accentColor: '',
                prototype: '',
                productImages: [''],
                align: 'left',
            });
            setEditingProject(null);
            setIsAddingProject(false);
            setValidationErrors({});

            // Refresh projects list
            fetchProjects();
        } catch (error) {
            console.error('Error saving project:', error);
            setErrorMessage('Failed to save project. Please try again.');
        }
    };

    const handleDelete = async (projectId) => {
        if (window.confirm('Are you sure you want to delete this project?')) {
            try {
                await deleteDoc(doc(db, 'projects', projectId));
                setSuccessMessage('Project deleted successfully!');
                await fetchProjects();
            } catch (error) {
                console.error('Error deleting project:', error);
                setErrorMessage('Failed to delete project');
            }
        }
    };

    const renderPreview = () => {
        if (!previewMode) return null;

        return (
            <div className="preview-container">
                <div className="preview-header">
                    <h3>Preview</h3>
                    <div className="preview-tabs">
                        <button
                            className={previewCollection === 'portfolio' ? 'active' : ''}
                            onClick={() => setPreviewCollection('portfolio')}
                        >
                            Portfolio View
                        </button>
                        <button
                            className={previewCollection === 'products' ? 'active' : ''}
                            onClick={() => setPreviewCollection('products')}
                        >
                            Products View
                        </button>
                    </div>
                </div>
                <div className="preview-content">
                    <div className="preview-project">
                        <h2>{formData.name || 'Project Name'}</h2>
                        <span>{formData.type === 'mobile' ? 'Mobile Application' : 'Website'}</span>
                        <p>{formData.intro || 'Project Introduction'}</p>
                        {formData.banner && (
                            <div className="preview-image">
                                <img src={formData.banner && formData.banner.url ? formData.banner.url : formData.banner} alt="Preview" />
                            </div>
                        )}
                        {formData.features.length > 0 && (
                            <div className="preview-features">
                                <h4>Features:</h4>
                                <ul>
                                    {formData.features.map((feature, index) => (
                                        <li key={index}>
                                            <strong>{feature.title}</strong>: {feature.description}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Helper for uploading a file to Firebase Storage
    const uploadFile = (file, path, onProgress) => {
        return new Promise((resolve, reject) => {
            const storageRef = ref(storage, path);
            const uploadTask = uploadBytesResumable(storageRef, file);
            uploadTask.on('state_changed', 
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    if (onProgress) onProgress(progress);
                }, 
                (error) => reject(error),
                () => {
                    getDownloadURL(uploadTask.snapshot.ref).then(resolve).catch(reject);
                }
            );
        });
    };

    // Upload client logo
    const handleLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploadingLogo(true);
        setLogoUploadProgress(0);
        
        try {
            await uploadFile(
                file,
                `clients/${Date.now()}_${file.name}`,
                (progress) => setLogoUploadProgress(progress)
            );
            
            setSuccessMessage('Logo uploaded successfully!');
            await fetchClientLogos(); // Refresh the list
        } catch (error) {
            console.error('Error uploading logo:', error);
            setErrorMessage('Failed to upload logo');
        } finally {
            setUploadingLogo(false);
            setLogoUploadProgress(0);
        }
    };

    // Delete client logo
    const handleDeleteLogo = async (logoPath, logoName) => {
        if (window.confirm(`Are you sure you want to delete ${logoName}?`)) {
            try {
                const logoRef = ref(storage, logoPath);
                await deleteObject(logoRef);
                setSuccessMessage('Logo deleted successfully!');
                await fetchClientLogos(); // Refresh the list
            } catch (error) {
                console.error('Error deleting logo:', error);
                setErrorMessage('Failed to delete logo');
            }
        }
    };

    // Toggle logo visibility
    const handleToggleLogoVisibility = async (logo) => {
        try {
            const newVisibility = !logo.visible;
            
            // Update in Firestore - use setDoc to create if doesn't exist
            const visibilityRef = doc(db, 'clientLogosVisibility', logo.name);
            await setDoc(visibilityRef, {
                visible: newVisibility,
                updatedAt: serverTimestamp()
            });
            
            // Update local state
            setClientLogos(prevLogos => 
                prevLogos.map(l => 
                    l.name === logo.name 
                        ? { ...l, visible: newVisibility }
                        : l
                )
            );
            
            setSuccessMessage(`Logo ${newVisibility ? 'shown' : 'hidden'} successfully!`);
        } catch (error) {
            console.error('Error toggling logo visibility:', error);
            setErrorMessage('Failed to update logo visibility');
        }
    };

    // Render client logos management section
    const renderClientLogos = () => {
        return (
            <div className="client-logos-section" style={{
                padding: '20px',
                backgroundColor: '#f8f9fa',
                borderRadius: '12px',
                margin: '20px 0'
            }}>
                <div className="section-header" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '30px',
                    paddingBottom: '20px',
                    borderBottom: '2px solid #e9ecef'
                }}>
                    <div>
                        <h2 style={{
                            margin: '0 0 8px 0',
                            color: '#2c3e50',
                            fontSize: '28px',
                            fontWeight: '600'
                        }}>Clients' Logos Management</h2>
                        <p style={{
                            margin: '0',
                            color: '#6c757d',
                            fontSize: '14px'
                        }}>
                            Manage client logos that appear in the scrolling banner. Upload, view, and delete logos as needed.
                        </p>
                    </div>
                    <div className="upload-section" style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: '10px'
                    }}>
                        <input
                            type="file"
                            id="logo-upload"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={handleLogoUpload}
                            disabled={uploadingLogo}
                        />
                        <label htmlFor="logo-upload" className="upload-btn" style={{ 
                            background: 'linear-gradient(135deg, #4D92CE 0%, #357ABD 100%)',
                            color: '#fff',
                            padding: '12px 24px',
                            borderRadius: '8px',
                            cursor: uploadingLogo ? 'not-allowed' : 'pointer',
                            opacity: uploadingLogo ? 0.7 : 1,
                            display: 'inline-block',
                            fontSize: '14px',
                            fontWeight: '500',
                            boxShadow: '0 2px 8px rgba(77, 146, 206, 0.3)',
                            transition: 'all 0.3s ease',
                            border: 'none'
                        }}
                        onMouseOver={(e) => {
                            if (!uploadingLogo) {
                                e.target.style.transform = 'translateY(-2px)';
                                e.target.style.boxShadow = '0 4px 12px rgba(77, 146, 206, 0.4)';
                            }
                        }}
                        onMouseOut={(e) => {
                            if (!uploadingLogo) {
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 2px 8px rgba(77, 146, 206, 0.3)';
                            }
                        }}
                        >
                            {uploadingLogo ? (
                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <div style={{
                                        width: '16px',
                                        height: '16px',
                                        border: '2px solid #fff',
                                        borderTop: '2px solid transparent',
                                        borderRadius: '50%',
                                        animation: 'spin 1s linear infinite'
                                    }}></div>
                                    Uploading...
                                </span>
                            ) : (
                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                        <polyline points="7,10 12,15 17,10"/>
                                        <line x1="12" y1="15" x2="12" y2="3"/>
                                    </svg>
                                    Upload New Logo
                                </span>
                            )}
                        </label>
                        {uploadingLogo && (
                            <div className="upload-progress" style={{ width: '200px' }}>
                                <div style={{
                                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '12px',
                    color: '#6c757d',
                    marginBottom: '4px'
                }}>
                                    <span>Uploading...</span>
                                    <span>{Math.round(logoUploadProgress)}%</span>
                                </div>
                                <div className="progress-bar" style={{ 
                                    width: '100%', 
                                    height: '8px', 
                                    background: '#e9ecef', 
                                    borderRadius: '4px', 
                                    overflow: 'hidden'
                                }}>
                                    <div className="progress" style={{ 
                                        width: `${logoUploadProgress}%`, 
                                        height: '100%', 
                                        background: 'linear-gradient(90deg, #4D92CE 0%, #357ABD 100%)', 
                                        transition: 'width 0.3s ease',
                                        borderRadius: '4px'
                                    }} />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {logosLoading ? (
                    <div className="loading" style={{
                        textAlign: 'center',
                        padding: '40px',
                        color: '#6c757d',
                        fontSize: '16px'
                    }}>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            border: '3px solid #e9ecef',
                            borderTop: '3px solid #4D92CE',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            margin: '0 auto 16px'
                        }}></div>
                        Loading client logos...
                    </div>
                ) : (
                    <div className="logos-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                        gap: '20px',
                        marginTop: '20px'
                    }}>
                        {clientLogos.map((logo, index) => (
                            <div key={index} className="logo-card" style={{
                                background: '#fff',
                                borderRadius: '12px',
                                padding: '20px',
                                boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
                                border: '1px solid #e9ecef',
                                transition: 'all 0.3s ease',
                                position: 'relative',
                                overflow: 'hidden'
                            }}
                            onMouseOver={(e) => {
                                e.target.style.transform = 'translateY(-4px)';
                                e.target.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
                            }}
                            onMouseOut={(e) => {
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 2px 12px rgba(0, 0, 0, 0.08)';
                            }}
                            >
                                <div className="logo-preview" style={{
                                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginBottom: '16px',
                    padding: '20px',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    border: '2px dashed #dee2e6'
                                }}>
                                    <img 
                                        src={logo.url} 
                                        alt={logo.name}
                                        style={{
                                            width: '80px',
                                            height: '80px',
                                            filter: 'grayscale(100%)',
                                            objectFit: 'contain',
                                            transition: 'all 0.3s ease'
                                        }}
                                        onMouseOver={(e) => {
                                            e.target.style.filter = 'grayscale(0%)';
                                            e.target.style.transform = 'scale(1.1)';
                                        }}
                                        onMouseOut={(e) => {
                                            e.target.style.filter = 'grayscale(100%)';
                                            e.target.style.transform = 'scale(1)';
                                        }}
                                    />
                                </div>
                                <div className="logo-info">
                                    <p className="logo-name" style={{
                                        margin: '0 0 12px 0',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        color: '#2c3e50',
                                        textAlign: 'center',
                                        wordBreak: 'break-word'
                                    }}>{logo.name}</p>
                                    <div className="logo-actions" style={{
                                        display: 'flex',
                        gap: '8px',
                        justifyContent: 'center'
                                    }}>
                                        <button 
                                            className={`visibility-btn ${logo.visible ? 'visible' : 'hidden'}`}
                                            onClick={() => handleToggleLogoVisibility(logo)}
                                            title={logo.visible ? 'Hide logo' : 'Show logo'}
                                            style={{
                                                background: logo.visible ? '#28a745' : '#6c757d',
                                                color: '#fff',
                                                border: 'none',
                                                padding: '8px 16px',
                                                borderRadius: '6px',
                                                fontSize: '12px',
                                                fontWeight: '500',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease',
                                                flex: 1
                                            }}
                                            onMouseOver={(e) => {
                                                e.target.style.background = logo.visible ? '#218838' : '#5a6268';
                                                e.target.style.transform = 'translateY(-1px)';
                                            }}
                                            onMouseOut={(e) => {
                                                e.target.style.background = logo.visible ? '#28a745' : '#6c757d';
                                                e.target.style.transform = 'translateY(0)';
                                            }}
                                        >
                                            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                                {logo.visible ? (
                                                    <>
                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                                            <circle cx="12" cy="12" r="3"/>
                                                        </svg>
                                                        Show
                                                    </>
                                                ) : (
                                                    <>
                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                                                            <line x1="1" y1="1" x2="23" y2="23"/>
                                                        </svg>
                                                        Hide
                                                    </>
                                                )}
                                            </span>
                                        </button>
                                        <button 
                                            className="delete-btn"
                                            onClick={() => handleDeleteLogo(logo.path, logo.name)}
                                            title="Delete logo"
                                            style={{
                                                background: '#dc3545',
                                                color: '#fff',
                                                border: 'none',
                                                padding: '8px 16px',
                                                borderRadius: '6px',
                                                fontSize: '12px',
                                                fontWeight: '500',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease',
                                                flex: 1
                                            }}
                                            onMouseOver={(e) => {
                                                e.target.style.background = '#c82333';
                                                e.target.style.transform = 'translateY(-1px)';
                                            }}
                                            onMouseOut={(e) => {
                                                e.target.style.background = '#dc3545';
                                                e.target.style.transform = 'translateY(0)';
                                            }}
                                        >
                                            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <polyline points="3,6 5,6 21,6"/>
                                                    <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"/>
                                                </svg>
                                                Delete
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                        {clientLogos.length === 0 && (
                            <div className="no-logos" style={{
                                gridColumn: '1 / -1',
                                textAlign: 'center',
                                padding: '60px 20px',
                                background: '#fff',
                                borderRadius: '12px',
                                border: '2px dashed #dee2e6',
                                color: '#6c757d'
                            }}>
                                <div style={{
                                    width: '64px',
                                    height: '64px',
                                    margin: '0 auto 16px',
                                    background: '#f8f9fa',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                        <circle cx="8.5" cy="8.5" r="1.5"/>
                                        <polyline points="21,15 16,10 5,21"/>
                                    </svg>
                                </div>
                                <h3 style={{ margin: '0 0 8px 0', color: '#495057', fontSize: '18px' }}>
                                    No Client Logos Found
                                </h3>
                                <p style={{ margin: '0', fontSize: '14px' }}>
                                    Upload your first client logo to get started. Logos will appear in the scrolling banner on your website.
                                </p>
                            </div>
                        )}
                    </div>
                )}

                <style jsx>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    };

    // Remove banner image
    const removeBannerImage = () => {
        setFormData({ ...formData, banner: null });
    };

    // Remove product image
    const removeProductImage = (index) => {
        const newImages = formData.productImages.filter((_, i) => i !== index);
        setFormData({ ...formData, productImages: newImages });
        const newUploading = uploadingProductImages.filter((_, i) => i !== index);
        setUploadingProductImages(newUploading);
        const newProgress = productImageUploadProgress.filter((_, i) => i !== index);
        setProductImageUploadProgress(newProgress);
    };

    // Remove project image
    const removeProjectImage = () => {
        setFormData({ ...formData, image: null });
    };

    // Check if any image is uploading
    const anyUploading = uploadingBanner || uploadingProductImages.some(Boolean) || uploadingProjectImage;

    const renderForm = () => {
        if (!isAddingProject) return null;

        return (
            <div className="admin-form-container">
                <div className="form-header">
                    <h2>{editingProject ? 'Edit Project' : 'Add New Project'}</h2>
                    <div className="form-actions">
                        <button
                            className="preview-btn"
                            onClick={() => setPreviewMode(!previewMode)}
                        >
                            {previewMode ? 'Hide Preview' : 'Show Preview'}
                        </button>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="admin-form">
                    <div className="form-section">
                        <h3>Basic Information</h3>
                        <div className="form-group">
                            <label>
                                Project Name *
                                {validationErrors.name && <span className="error">{validationErrors.name}</span>}
                            </label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Enter project name"
                            />
                        </div>

                        <div className="form-group">
                            <label>
                                Project Category *
                                {validationErrors.category && <span className="error">{validationErrors.category}</span>}
                            </label>
                            <input
                                type="text"
                                value={formData.category}
                                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                placeholder="Enter project category (e.g., community app)"
                            />
                        </div>

                        <div className="form-group">
                            <label>
                                Project Description *
                                {validationErrors.description && <span className="error">{validationErrors.description}</span>}
                            </label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Enter project description"
                            />
                        </div>

                        {/* Project Image Upload */}
                        <div className="form-group image-upload-group">
                            <label>Project Image *</label>
                            <div className="image-upload-ui">
                                {formData.image && formData.image.url ? (
                                    <div className="image-preview-container" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <img src={formData.image.url} alt="Project preview" className="image-preview" style={{ maxWidth: 120, borderRadius: 8, border: '1px solid #eee' }} />
                                        <div style={{ fontSize: 12, color: '#555', marginLeft: 8 }}>{formData.image.name}</div>
                                        <button type="button" className="remove-btn" onClick={removeProjectImage} style={{ marginLeft: 8 }}>Remove</button>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <input
                                            type="file"
                                            id="project-image-upload"
                                            accept="image/*"
                                            style={{ display: 'none' }}
                                            onChange={async (e) => {
                                                const file = e.target.files[0];
                                                if (!file) return;
                                                setUploadingProjectImage(true);
                                                setProjectImageUploadProgress(0);
                                                try {
                                                    const url = await uploadFile(
                                                        file,
                                                        `projectImages/${Date.now()}_${file.name}`,
                                                        (progress) => setProjectImageUploadProgress(progress)
                                                    );
                                                    setFormData({ ...formData, image: { url, name: file.name } });
                                                } catch (err) {
                                                    setErrorMessage('Failed to upload project image');
                                                }
                                                setUploadingProjectImage(false);
                                            }}
                                            disabled={uploadingProjectImage}
                                        />
                                        <label htmlFor="project-image-upload" className="upload-btn" style={{ background: '#4D92CE', color: '#fff', padding: '8px 18px', borderRadius: 6, cursor: uploadingProjectImage ? 'not-allowed' : 'pointer', opacity: uploadingProjectImage ? 0.7 : 1 }}>
                                            {uploadingProjectImage ? 'Uploading...' : 'Upload Project Image'}
                                        </label>
                                        {uploadingProjectImage && (
                                            <>
                                                <div style={{ fontSize: 12, color: '#555', marginLeft: 8 }}>Uploading: {formData.image && formData.image.name ? formData.image.name : ''}</div>
                                                <div className="progress-bar" style={{ width: 80, height: 8, background: '#eee', borderRadius: 4, overflow: 'hidden', marginLeft: 8 }}>
                                                    <div className="progress" style={{ width: `${projectImageUploadProgress}%`, height: '100%', background: '#4D92CE', transition: 'width 0.2s' }} />
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                                {validationErrors.image && <div className="error" style={{ color: '#dc3545', marginTop: 6 }}>{validationErrors.image}</div>}
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h3>Project Type & Display</h3>
                        <div className="form-group">
                            <label>Project Type</label>
                            <div className="radio-group">
                                <label className="radio-label">
                                    <input
                                        type="radio"
                                        value="mobile"
                                        checked={formData.type === 'mobile'}
                                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                    />
                                    <span className="radio-text">Mobile Application</span>
                                </label>
                                <label className="radio-label">
                                    <input
                                        type="radio"
                                        value="website"
                                        checked={formData.type === 'website'}
                                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                    />
                                    <span className="radio-text">Website</span>
                                </label>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Display Location</label>
                            <div className="collection-selector">
                                <div
                                    className={`collection-option ${formData.collection === 'portfolio' ? 'active' : ''}`}
                                    onClick={() => setFormData({ ...formData, collection: 'portfolio' })}
                                >
                                    <h4>Portfolio</h4>
                                    <p>Showcase your work in the portfolio section</p>
                                </div>
                                <div
                                    className={`collection-option ${formData.collection === 'products' ? 'active' : ''}`}
                                    onClick={() => setFormData({ ...formData, collection: 'products' })}
                                >
                                    <h4>Products</h4>
                                    <p>List your product in the products section</p>
                                </div>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Project Alignment on {formData.collection === 'portfolio' ? 'Portfolio' : 'Products'} Page</label>
                            <div className="radio-group">
                                <label className="radio-label">
                                    <input
                                        type="radio"
                                        name="alignment"
                                        value="left"
                                        checked={formData.align === 'left'}
                                        onChange={(e) => setFormData({ ...formData, align: e.target.value })}
                                    />
                                    <span className="radio-text">Left</span>
                                </label>
                                <label className="radio-label">
                                    <input
                                        type="radio"
                                        name="alignment"
                                        value="right"
                                        checked={formData.align === 'right'}
                                        onChange={(e) => setFormData({ ...formData, align: e.target.value })}
                                    />
                                    <span className="radio-text">Right</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h3>Media & Content</h3>
                        <div className="form-group image-upload-group">
                            <label>Banner Image *</label>
                            <div className="image-upload-ui">
                                {formData.banner && formData.banner.url ? (
                                    <div className="image-preview-container" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <img src={formData.banner.url} alt="Banner preview" className="image-preview" style={{ maxWidth: 120, borderRadius: 8, border: '1px solid #eee' }} />
                                        <div style={{ fontSize: 12, color: '#555', marginLeft: 8 }}>{formData.banner.name}</div>
                                        <button type="button" className="remove-btn" onClick={removeBannerImage} style={{ marginLeft: 8 }}>Remove</button>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <input
                                            type="file"
                                            id="banner-upload"
                                            accept="image/*"
                                            style={{ display: 'none' }}
                                            onChange={async (e) => {
                                                const file = e.target.files[0];
                                                if (!file) return;
                                                setUploadingBanner(true);
                                                setBannerUploadProgress(0);
                                                try {
                                                    const url = await uploadFile(
                                                        file,
                                                        `banners/${Date.now()}_${file.name}`,
                                                        (progress) => setBannerUploadProgress(progress)
                                                    );
                                                    setFormData({ ...formData, banner: { url, name: file.name } });
                                                } catch (err) {
                                                    setErrorMessage('Failed to upload banner image');
                                                }
                                                setUploadingBanner(false);
                                            }}
                                            disabled={uploadingBanner}
                                        />
                                        <label htmlFor="banner-upload" className="upload-btn" style={{ background: '#4D92CE', color: '#fff', padding: '8px 18px', borderRadius: 6, cursor: uploadingBanner ? 'not-allowed' : 'pointer', opacity: uploadingBanner ? 0.7 : 1 }}>
                                            {uploadingBanner ? 'Uploading...' : 'Upload Banner'}
                                        </label>
                                        {uploadingBanner && (
                                            <>
                                                <div style={{ fontSize: 12, color: '#555', marginLeft: 8 }}>Uploading: {formData.banner && formData.banner.name ? formData.banner.name : ''}</div>
                                                <div className="progress-bar" style={{ width: 80, height: 8, background: '#eee', borderRadius: 4, overflow: 'hidden', marginLeft: 8 }}>
                                                    <div className="progress" style={{ width: `${bannerUploadProgress}%`, height: '100%', background: '#4D92CE', transition: 'width 0.2s' }} />
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                                {validationErrors.banner && <div className="error" style={{ color: '#dc3545', marginTop: 6 }}>{validationErrors.banner}</div>}
                            </div>
                        </div>

                        <div className="form-group">
                            <label>
                                Project Introduction *
                                {validationErrors.intro && <span className="error">{validationErrors.intro}</span>}
                            </label>
                            <textarea
                                value={formData.intro}
                                onChange={(e) => setFormData({ ...formData, intro: e.target.value })}
                                placeholder="Enter project introduction"
                            />
                        </div>
                    </div>

                    <div className="form-section">
                        <h3>Features (Optional)</h3>
                        <p className="section-description">Add key features of your project. This section is optional.</p>
                        {formData.features.map((feature, index) => (
                            <div key={index} className="feature-group">
                                <div className="form-group">
                                    <input
                                        type="text"
                                        value={feature.title}
                                        onChange={(e) => {
                                            const newFeatures = [...formData.features];
                                            newFeatures[index].title = e.target.value;
                                            setFormData({ ...formData, features: newFeatures });
                                        }}
                                        placeholder="Feature title"
                                    />
                                    <textarea
                                        value={feature.description}
                                        onChange={(e) => {
                                            const newFeatures = [...formData.features];
                                            newFeatures[index].description = e.target.value;
                                            setFormData({ ...formData, features: newFeatures });
                                        }}
                                        placeholder="Feature description"
                                    />
                                    <button
                                        type="button"
                                        className="remove-btn"
                                        onClick={() => {
                                            const newFeatures = formData.features.filter((_, i) => i !== index);
                                            setFormData({ ...formData, features: newFeatures });
                                        }}
                                    >
                                        Remove Feature
                                    </button>
                                </div>
                            </div>
                        ))}
                        <button
                            type="button"
                            className="add-btn"
                            onClick={() => setFormData({
                                ...formData,
                                features: [...formData.features, { title: '', description: '' }]
                            })}
                        >
                            Add Feature
                        </button>
                    </div>

                    <div className="form-section">
                        <h3>Platforms *</h3>
                        {validationErrors.platforms && <span className="error">{validationErrors.platforms}</span>}
                        {formData.platforms.map((platform, index) => (
                            <div key={index} className="form-group">
                                <div className="array-input-group">
                                    <input
                                        type="text"
                                        value={platform}
                                        onChange={(e) => {
                                            const newPlatforms = [...formData.platforms];
                                            newPlatforms[index] = e.target.value;
                                            setFormData({ ...formData, platforms: newPlatforms });
                                        }}
                                        placeholder="Enter platform"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const newPlatforms = formData.platforms.filter((_, i) => i !== index);
                                            setFormData({ ...formData, platforms: newPlatforms });
                                        }}
                                    >
                                        Remove
                                    </button>
                                </div>
                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={() => setFormData({
                                ...formData,
                                platforms: [...formData.platforms, '']
                            })}
                        >
                            Add Platform
                        </button>
                    </div>

                    <div className="form-section">
                        <h3>Colors *</h3>
                        {validationErrors.primaryColor && <span className="error">{validationErrors.primaryColor}</span>}
                        {validationErrors.secondaryColor && <span className="error">{validationErrors.secondaryColor}</span>}
                        {validationErrors.accentColor && <span className="error">{validationErrors.accentColor}</span>}
                        <div className="form-group">
                            <label>Primary Color *</label>
                            <input
                                type="text"
                                value={formData.primaryColor}
                                onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                                placeholder="#FF0000"
                            />
                            <div className="color-preview" style={{ backgroundColor: formData.primaryColor }}></div>
                        </div>
                        <div className="form-group">
                            <label>Secondary Color *</label>
                            <input
                                type="text"
                                value={formData.secondaryColor}
                                onChange={(e) => setFormData({ ...formData, secondaryColor: e.target.value })}
                                placeholder="#00FF00"
                            />
                            <div className="color-preview" style={{ backgroundColor: formData.secondaryColor }}></div>
                        </div>
                        <div className="form-group">
                            <label>Accent Color (Optional)</label>
                            <input
                                type="text"
                                value={formData.accentColor}
                                onChange={(e) => setFormData({ ...formData, accentColor: e.target.value })}
                                placeholder="#0000FF"
                            />
                            <div className="color-preview" style={{ backgroundColor: formData.accentColor }}></div>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Prototype Iframe Code *</label>
                        {validationErrors.prototype && <span className="error">{validationErrors.prototype}</span>}
                        <textarea
                            value={formData.prototype}
                            onChange={(e) => setFormData({ ...formData, prototype: e.target.value })}
                            placeholder="Enter prototype iframe code"
                        />
                    </div>

                    <div className="form-section">
                        <h3>Product Images *</h3>
                        {validationErrors.productImages && <span className="error">{validationErrors.productImages}</span>}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                            {formData.productImages.map((imgObj, index) => (
                                <div key={index} className="form-group image-upload-group" style={{ minWidth: 120 }}>
                                    <div className="image-upload-ui">
                                        {imgObj && imgObj.url ? (
                                            <div className="image-preview-container" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <img src={imgObj.url} alt={`Product ${index + 1}`} className="image-preview" style={{ maxWidth: 80, borderRadius: 8, border: '1px solid #eee' }} />
                                                <div style={{ fontSize: 12, color: '#555', marginLeft: 4 }}>{imgObj.name}</div>
                                                <button type="button" className="remove-btn" onClick={() => removeProductImage(index)} style={{ marginLeft: 4 }}>Remove</button>
                                            </div>
                                        ) : (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <input
                                                    type="file"
                                                    id={`product-upload-${index}`}
                                                    accept="image/*"
                                                    style={{ display: 'none' }}
                                                    onChange={async (e) => {
                                                        const file = e.target.files[0];
                                                        if (!file) return;
                                                        // Set uploading state for this image
                                                        const newUploading = [...uploadingProductImages];
                                                        newUploading[index] = true;
                                                        setUploadingProductImages(newUploading);
                                                        const newProgress = [...productImageUploadProgress];
                                                        newProgress[index] = 0;
                                                        setProductImageUploadProgress(newProgress);
                                                        try {
                                                            const url = await uploadFile(
                                                                file,
                                                                `productImages/${Date.now()}_${file.name}`,
                                                                (progress) => {
                                                                    const updated = [...productImageUploadProgress];
                                                                    updated[index] = progress;
                                                                    setProductImageUploadProgress(updated);
                                                                }
                                                            );
                                                            const newImages = [...formData.productImages];
                                                            newImages[index] = { url, name: file.name };
                                                            setFormData({ ...formData, productImages: newImages });
                                                        } catch (err) {
                                                            setErrorMessage('Failed to upload product image');
                                                        }
                                                        // Reset uploading state
                                                        const doneUploading = [...uploadingProductImages];
                                                        doneUploading[index] = false;
                                                        setUploadingProductImages(doneUploading);
                                                    }}
                                                    disabled={uploadingProductImages[index]}
                                                />
                                                <label htmlFor={`product-upload-${index}`} className="upload-btn" style={{ background: '#4D92CE', color: '#fff', padding: '6px 14px', borderRadius: 6, cursor: uploadingProductImages[index] ? 'not-allowed' : 'pointer', opacity: uploadingProductImages[index] ? 0.7 : 1 }}>
                                                    {uploadingProductImages[index] ? 'Uploading...' : 'Upload Image'}
                                                </label>
                                                {uploadingProductImages[index] && (
                                                    <>
                                                        <div style={{ fontSize: 12, color: '#555', marginLeft: 4 }}>Uploading: {imgObj && imgObj.name ? imgObj.name : ''}</div>
                                                        <div className="progress-bar" style={{ width: 60, height: 8, background: '#eee', borderRadius: 4, overflow: 'hidden', marginLeft: 4 }}>
                                                            <div className="progress" style={{ width: `${productImageUploadProgress[index] || 0}%`, height: '100%', background: '#4D92CE', transition: 'width 0.2s' }} />
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                            <button
                                type="button"
                                className="add-btn"
                                style={{ background: '#4D92CE', color: '#fff', padding: '8px 18px', borderRadius: 6, cursor: 'pointer', alignSelf: 'flex-start' }}
                                onClick={() => {
                                    setFormData({
                                        ...formData,
                                        productImages: [...formData.productImages, {}]
                                    });
                                    setUploadingProductImages([...uploadingProductImages, false]);
                                    setProductImageUploadProgress([...productImageUploadProgress, 0]);
                                }}
                            >
                                Add Image
                            </button>
                        </div>
                    </div>

                    <div className="form-actions">
                        <button type="submit" className="submit-btn" disabled={loading || anyUploading}>
                            {loading ? 'Saving...' : anyUploading ? 'Uploading...' : editingProject ? 'Update Project' : 'Save Project'}
                        </button>
                        <button type="button" className="cancel-btn" onClick={() => {
                            setIsAddingProject(false);
                            setEditingProject(null);
                            setFormData({
                                name: '',
                                type: 'mobile',
                                collection: 'portfolio',
                                category: '',
                                description: '',
                                banner: '',
                                intro: '',
                                features: [],
                                platforms: [''],
                                primaryColor: '',
                                secondaryColor: '',
                                accentColor: '',
                                prototype: '',
                                productImages: [''],
                                align: 'left',
                            });
                        }}>
                            Cancel
                        </button>
                    </div>
                </form>

                {renderPreview()}
            </div>
        );
    };



    if (loading) {
        return <div className="loading">Loading...</div>;
    }

    return (
        <div className="admin-dashboard">
            <h1>Admin Dashboard</h1>

            {successMessage && <div className="success-message">{successMessage}</div>}
            {errorMessage && <div className="error-message">{errorMessage}</div>}

            <div className="dashboard-controls">
                <div className="tab-buttons">
                    <button
                        className={activeTab === 'portfolio' ? 'active' : ''}
                        onClick={() => setActiveTab('portfolio')}
                    >
                        Portfolio
                    </button>
                    <button
                        className={activeTab === 'products' ? 'active' : ''}
                        onClick={() => setActiveTab('products')}
                    >
                        Products
                    </button>
                    <button
                        className={activeTab === 'testimonials' ? 'active' : ''}
                        onClick={() => setActiveTab('testimonials')}
                    >
                        Testimonials
                    </button>
                    <button
                        className={activeTab === 'clients-logos' ? 'active' : ''}
                        onClick={() => setActiveTab('clients-logos')}
                    >
                        Clients' Logos
                    </button>
                </div>
                {/* Add Project/Product Button */}
                {(activeTab === 'portfolio' || activeTab === 'products') && (
                    <div style={{ marginTop: 20, marginBottom: 20 }}>
                        <button
                            className="add-btn"
                            style={{ background: '#4D92CE', color: '#fff', padding: '10px 24px', borderRadius: 6, fontWeight: 500, fontSize: 16, cursor: 'pointer' }}
                            onClick={() => {
                                setIsAddingProject(true);
                                setEditingProject(null);
                            }}
                        >
                            {activeTab === 'portfolio' ? 'Add Portfolio Project' : 'Add Product'}
                        </button>
                    </div>
                )}
            </div>
            {activeTab === 'testimonials' ? (
                <AdminTestimonials />
            ) : activeTab === 'clients-logos' ? (
                renderClientLogos()
            ) : isAddingProject ? (
                renderForm()
            ) : (
                <>
                    <div className="filter-sections">
                        <h3>View Projects By:</h3>
                        <div className="type-buttons">
                            <button
                                className={activeType === 'mobile' ? 'active' : ''}
                                onClick={() => setActiveType('mobile')}
                            >
                                Mobile Applications
                            </button>
                            <button
                                className={activeType === 'websites' ? 'active' : ''}
                                onClick={() => setActiveType('websites')}
                            >
                                Websites
                            </button>
                        </div>
                    </div>
                    <div className="project-list-section">
                        {listLoading ? (
                            <div className="loading">Loading projects...</div>
                        ) : errorMessage ? (
                            <div className="error-message">{errorMessage}</div>
                        ) : (
                            <>
                                <h2 className="section-title">
                                    {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} - {activeType === 'mobile' ? 'Mobile Applications' : 'Websites'}
                                </h2>
                                {projectsList.length > 0 ? (
                                    <div className="projects-grid">
                                        {projectsList.map((project) => (
                                            <div key={project.id} className="project-card">
                                                <img src={project.banner} alt={project.name} className="project-image" />
                                                <div className="project-info">
                                                    <h3>{project.name}</h3>
                                                    <p className="project-category">{project.type === 'mobile' ? 'Mobile Application' : 'Website'}</p>
                                                    <p className="project-description">{project.intro || 'No introduction provided'}</p>
                                                    <div className="project-actions">
                                                        <button onClick={() => handleEdit(project)} className="edit-btn">Edit</button>
                                                        <button onClick={() => handleDelete(project.id)} className="delete-btn">Delete</button>
                                                        <button
                                                            onClick={() => handleToggleVisibility(project.id, project.visible)}
                                                            className={`visibility-btn ${project.visible ? 'visible' : 'hidden'}`}
                                                        >
                                                            {project.visible ? 'Hide' : 'Show'}
                                                        </button>
                                                        <span className={`project-status ${project.status}`}>{project.status}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="no-projects">No projects found for this category.</div>
                                )}
                            </>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

export default AdminDashboard; 